import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, Pagination, Spinner } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { useDebounced } from '@shared/lib/use-debounced';
import { useProductsQuery, type ProductStatus } from '@entities/product';
import { ProductsTable, ProductsFilters } from '@features/product-list';

const ALL_STATUSES: ProductStatus[] = [
  'DRAFT',
  'PENDING_MODERATION',
  'PUBLISHED',
  'REJECTED',
  'ARCHIVED',
];

export function ProductListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const statusParam = params.get('status') as ProductStatus | null;
  const stockOnly = params.get('stock') === 'true';
  const initialQ = params.get('q') ?? '';
  const initialPage = Number(params.get('page') ?? 0);

  const [q, setQ] = useState(initialQ);
  const [page, setPage] = useState(initialPage);
  const debouncedQ = useDebounced(q);

  const query = useMemo(
    () => ({
      status: statusParam ? [statusParam] : ALL_STATUSES,
      stock: stockOnly || undefined,
      q: debouncedQ || undefined,
      page,
      size: 20,
    }),
    [statusParam, stockOnly, debouncedQ, page],
  );

  const { data, isLoading, isError, error, refetch } = useProductsQuery(query);

  const updateParam = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('products.title')}</h1>
          <p className="text-sm opacity-70">{t('products.subtitle')}</p>
        </div>
        <Button onClick={() => navigate(ROUTES.productNew)}>{t('products.new')}</Button>
      </header>

      <ProductsFilters
        status={statusParam ?? undefined}
        q={q}
        onStatusChange={(s) => {
          setPage(0);
          updateParam({ status: s ?? null, page: '0' });
        }}
        onQueryChange={(value) => {
          setQ(value);
          setPage(0);
          updateParam({ q: value || null, page: '0' });
        }}
      />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="flex flex-col gap-3">
          <p>{t('products.loadFail', { message: error?.message ?? 'unknown' })}</p>
          <Button variant="outline" onClick={() => void refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : data && data.items.length === 0 ? (
        <EmptyState
          title={t('products.empty.title')}
          description={t('products.empty.description')}
          action={<Button onClick={() => navigate(ROUTES.productNew)}>{t('products.empty.action')}</Button>}
        />
      ) : data ? (
        <>
          <ProductsTable data={data.items} />
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            onPageChange={(p) => {
              setPage(p);
              updateParam({ page: String(p) });
            }}
          />
        </>
      ) : null}
    </div>
  );
}
